/* global BigInt */
import { encodeFunctionData, decodeEventLog, parseAbi } from 'viem';
import BalanceOfAbi from '../../../src/utils/BalanceOfAbi.json';
import { ALCHEMY_API_KEY, usdr_token, usdt_token, RAIN_ARBI_CONTRACT_ADDRESS } from "../../utils/Enviroment";
import { useRecoilValue } from 'recoil';
import { userSmartAccount, userSmartAccountClient, userPublicClient } from '../../components/RecoilAtom';
import { signPreparedCalls } from '@account-kit/wallet-client';
import { useSessionPermissions } from '../checkSessionPermissions';
import { getTxHashAndReceipt, handleGasFeeWithBuffer, sendGasFeeAsUsdt, convertToMwei } from '../../utils/helpers';
import { Network, Alchemy } from 'alchemy-sdk';
import { TOKEN_USDT, TOKEN_USDR, TOKEN_RAIN, USDT_MOCK_VALUE, USDR_DECIMALS, USDT_DECIMALS } from '../../utils/constants';

// USDT stays in the map so existing callers keep working; unknown tokens fall back to USDR
const WITHDRAW_TOKENS = {
    [TOKEN_USDR]: { address: usdr_token, decimals: USDR_DECIMALS },
    [TOKEN_RAIN]: { address: RAIN_ARBI_CONTRACT_ADDRESS, decimals: 18 },
    [TOKEN_USDT]: { address: usdt_token, decimals: USDT_DECIMALS },
};

// convertToMwei returns "0.00" for empty input, which BigInt can't parse
const toWei = (value, decimals) => {
    const wei = convertToMwei(value, decimals);
    return /^\d+$/.test(wei) ? BigInt(wei) : 0n;
};

export const getWithdrawToken = (tokenType) => WITHDRAW_TOKENS[tokenType] ?? WITHDRAW_TOKENS[TOKEN_USDR];

const ERC20_TRANSFER_ABI = parseAbi([
    'event Transfer(address indexed from, address indexed to, uint256 value)',
]);

// A withdrawal counts only if the receipt holds a Transfer of `tokenAddress` to
// the recipient; returns the transferred amount or null
export const readConfirmedTransfer = (receipt, tokenAddress, decimals, toAddress) => {
    const status = receipt?.status;
    const succeeded = status === 'success' || status === 1 || status === 1n || status === '0x1';
    if (!succeeded || !tokenAddress) return null;

    const token = tokenAddress.toLowerCase();
    const recipient = toAddress?.toLowerCase();
    let total = 0n;
    let found = false;
    for (const log of receipt?.logs ?? []) {
        if (String(log?.address ?? '').toLowerCase() !== token) continue;
        try {
            const { args } = decodeEventLog({ abi: ERC20_TRANSFER_ABI, data: log.data, topics: log.topics });
            if (recipient && String(args.to).toLowerCase() !== recipient) continue;
            total += args.value;
            found = true;
        } catch {
            continue;
        }
    }
    return found ? { amount: total, decimals } : null;
};

export const useWithdrawalAmountHook = () => {
    const settings = {
        apiKey: ALCHEMY_API_KEY,
        network: Network.ARB_MAINNET,
    };
    const alchemy = new Alchemy(settings);
    const smartAccount = useRecoilValue(userSmartAccount);
    const targetABI = BalanceOfAbi;
    const smartAccountClient = useRecoilValue(userSmartAccountClient);
    const publicClient = useRecoilValue(userPublicClient);
    const { grantPermissions } = useSessionPermissions()

    const calculateGasBuffer = async (tokenAddress, withdrawalAddress, withdrawalAmountInWei, isUsdr) => {
        const callArgs = [{
            to: tokenAddress,
            data: encodeFunctionData({
                abi: targetABI,
                functionName: "transfer",
                args: [withdrawalAddress, withdrawalAmountInWei]
            })
        },
        ...(isUsdr ? [{
            to: usdr_token,
            data: sendGasFeeAsUsdt(USDT_MOCK_VALUE)
        }] : [])]
        const gasFee = await handleGasFeeWithBuffer(smartAccount, callArgs, alchemy)
        return gasFee;
    }

    // Plain token transfer from the smart account; gas fees only deducted for USDR
    const withdrawalAmountTransaction = async (withdrawalAddress, withdrawalAmount, tokenType, tokenBalance) => {
        const { address: tokenAddress, decimals } = getWithdrawToken(tokenType);
        const isUsdr = tokenType === TOKEN_USDR;
        const withdrawalAmountInWei = toWei(withdrawalAmount, decimals);
        const tokenBalanceInWei = toWei(tokenBalance, decimals);
        const estimatedGasFee = await calculateGasBuffer(tokenAddress, withdrawalAddress, withdrawalAmountInWei, isUsdr);
        const estimatedGasFeeInWei = toWei(estimatedGasFee, USDR_DECIMALS);
        let latestAmountToWithdraw = withdrawalAmountInWei;

        // Withdrawing (almost) the whole USDR balance: take the fee out of the amount
        if (isUsdr && tokenBalanceInWei <= withdrawalAmountInWei + estimatedGasFeeInWei) {
            latestAmountToWithdraw = withdrawalAmountInWei - estimatedGasFeeInWei;
        }

        try {
            const { userPermissions: permissions, userSessionKey: sessionKey } = await grantPermissions()
            const callArgs = [{
                to: tokenAddress,
                data: encodeFunctionData({
                    abi: targetABI,
                    functionName: "transfer",
                    args: [withdrawalAddress, latestAmountToWithdraw]
                })
            },
            ...(isUsdr ? [{
                to: usdr_token,
                data: sendGasFeeAsUsdt(+estimatedGasFee)
            }] : [])]
            const preparedCalls = await smartAccountClient.prepareCalls({
                calls: callArgs,
                from: smartAccount,
                capabilities: { permissions },
            });
            const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);
            const sendResult = await smartAccountClient.sendPreparedCalls({
                ...signedCalls,
                capabilities: { permissions },
            });
            const { preparedCallIds } = sendResult;
            const { txHash, receipt } = await getTxHashAndReceipt({
                preparedCallId: preparedCallIds[0],
                smartAccountClient,
                publicClient,
            });
            return { receipt, error: null, txHash };
        } catch (error) {
            console.error('Error in withdrawal transaction:', error);
            return { receipt: null, error, txHash: null };
        }
    };

    return { withdrawalAmountTransaction };
};
